/* iter561 — Dashboard Mode Context.
 *
 * Tri-level disclosure system for patient-facing terminology:
 *  - "easy"     → Лесен (default). Points, colors, friendly Bulgarian labels.
 *                  Hides advanced widgets behind "Виж повече" panel.
 *  - "standard" → Стандарт. Adds budget %, NIS percent, brand acronyms next
 *                  to Bulgarian descriptors. Power-user friendly.
 *  - "expert"   → Експертен. Full clinical readout: g DIL, NIS PIRU,
 *                  14-day exposure, formulas, references. For clinicians.
 *
 * Mode is persisted server-side via /api/preferences (preferences.dashboard_mode).
 * Optimistic UI: state updates immediately; server-sync is fire-and-forget.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";

const MODES = ["easy", "standard", "expert"];
const DEFAULT_MODE = "easy";
const STORAGE_KEY = "nutramilo_dashboard_mode";

const DashboardModeContext = createContext({
  mode: DEFAULT_MODE,
  setMode: () => {},
  isEasy: true,
  isStandard: false,
  isExpert: false,
});

export function DashboardModeProvider({ children }) {
  const { user, API } = useAuth();
  // Optimistic init: localStorage first (instant) → server overwrites on load
  const [mode, setModeState] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return MODES.includes(v) ? v : DEFAULT_MODE;
    } catch {
      return DEFAULT_MODE;
    }
  });

  // Pull authoritative mode from server preferences once auth is ready.
  useEffect(() => {
    if (!user) return;
    const token =
      localStorage.getItem("nutramilo_access_token") ||
      sessionStorage.getItem("nutramilo_access_token");
    if (!token) return;
    axios
      .get(`${API}/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        const m = r.data?.dashboard_mode;
        if (MODES.includes(m)) {
          setModeState((cur) => (m !== cur ? m : cur));
          try {
            localStorage.setItem(STORAGE_KEY, m);
          } catch {
            // ignore quota errors
          }
        }
      })
      .catch(() => {
        // silent — fall back to localStorage value
      });
  }, [user, API]);

  const setMode = useCallback(
    (newMode) => {
      if (!MODES.includes(newMode)) return;
      setModeState(newMode);
      try {
        localStorage.setItem(STORAGE_KEY, newMode);
      } catch {
        // ignore quota errors
      }
      const token =
        localStorage.getItem("nutramilo_access_token") ||
        sessionStorage.getItem("nutramilo_access_token");
      if (!token) return;
      axios
        .put(
          `${API}/preferences`,
          { dashboard_mode: newMode },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .catch(() => {
          // optimistic — leave state as-is even on server error
        });
    },
    [API]
  );

  const value = {
    mode,
    setMode,
    isEasy: mode === "easy",
    isStandard: mode === "standard",
    isExpert: mode === "expert",
  };

  return (
    <DashboardModeContext.Provider value={value}>
      {children}
    </DashboardModeContext.Provider>
  );
}

export function useDashboardMode() {
  return useContext(DashboardModeContext);
}
